import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/cn";
import { AvatarDaOrganizacao } from "./AvatarDaOrganizacao";

// A lista local não tinha Sócio nem Recepção, que apareciam como "PARTNER" e "RECEPTIONIST".
export { ROLE_LABEL } from "@bookdental/schemas";

/**
 * Onde o switcher está desenhado.
 *
 * - `barra`: centro da barra superior, no desktop. É a identidade da tela: logo (ou iniciais na cor
 *   da organização), nome em cima e "tipo · papel" embaixo, do tamanho do conteúdo (esticar com a
 *   tela só criaria um botão largo e vazio).
 * - `celular`: pílula larga no topo do celular, no lugar onde o LinkedIn põe a busca.
 */
export type VarianteDoSwitcher = "barra" | "celular";

export type OrganizacaoDoSwitcher = { id: string; nome: string; logo?: string | null; pessoal: boolean; detalhe: string | null };

type GatilhoProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variante: VarianteDoSwitcher;
  organizacao: OrganizacaoDoSwitcher;
  /** Há notificação não lida em OUTRA organização — a da ativa o sino já mostra. */
  avisoNasOutras: boolean;
};

/** O botão que abre o switcher. `forwardRef` porque o `PopoverTrigger` do Radix precisa do ref. */
export const GatilhoDaOrganizacao = forwardRef<HTMLButtonElement, GatilhoProps>(function GatilhoDaOrganizacao(
  { variante, organizacao: o, avisoNasOutras, className, ...resto },
  ref,
) {
  const barra = variante === "barra";

  return (
    <button
      ref={ref}
      type="button"
      title={barra ? [o.nome, o.detalhe].filter(Boolean).join(" · ") : undefined}
      className={cn(
        "relative flex h-11 items-center text-left transition-colors border border-zinc-200 bg-white hover:bg-zinc-50 dark:border-border dark:bg-card dark:hover:bg-muted",
        barra ? "max-w-[16rem] gap-2.5 rounded-lg py-1 pl-1.5 pr-3 lg:min-w-[15rem] lg:max-w-[22rem] xl:max-w-[30rem]" : "w-full gap-2.5 rounded-full py-1 pl-1 pr-3",
        className,
      )}
      {...resto}
    >
      <AvatarDaOrganizacao id={o.id} nome={o.nome} logo={o.logo} pessoal={o.pessoal} className={barra ? "h-8 w-8" : "h-9 w-9 rounded-full"} />
      <div className="min-w-0 flex-1">
        <p className={cn("truncate font-semibold leading-tight text-zinc-900 dark:text-foreground", barra ? "text-sm" : "text-[13px]")}>{o.nome}</p>
        {o.detalhe && <p className="truncate text-[11px] leading-tight text-zinc-500 dark:text-muted-foreground">{o.detalhe}</p>}
      </div>
      <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
      {avisoNasOutras && (
        <span
          aria-label="Há notificações não lidas em outras organizações"
          title="Há notificações não lidas em outras organizações"
          className={cn("absolute h-2 w-2 rounded-full bg-red-500", barra ? "right-1 top-1" : "right-2 top-1.5")}
        />
      )}
    </button>
  );
});

/** Uma organização na lista do switcher. */
export function LinhaDaOrganizacao({ organizacao: o, naoLidas = 0, selected }: { organizacao: OrganizacaoDoSwitcher; naoLidas?: number; selected?: boolean }) {
  return (
    <div className={cn("flex items-center gap-2.5 rounded-md px-2.5 py-2", selected ? "bg-brand-50 dark:bg-primary/10" : "hover:bg-zinc-50 dark:hover:bg-muted")}>
      <AvatarDaOrganizacao id={o.id} nome={o.nome} logo={o.logo} pessoal={o.pessoal} className="h-8 w-8" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-zinc-900 dark:text-foreground">{o.nome}</p>
        {o.detalhe && <p className="truncate text-[11px] text-zinc-500 dark:text-muted-foreground">{o.detalhe}</p>}
      </div>
      {/* Contagem em cinza de propósito: é informação ambiente, não alarme — vermelho fica pro
          sino (organização ativa) e pro pontinho do gatilho. */}
      {naoLidas > 0 && (
        <span
          aria-label={`${naoLidas} notificações não lidas`}
          className="inline-flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full bg-zinc-100 px-1 text-[10px] font-bold leading-none text-zinc-600 dark:bg-muted dark:text-muted-foreground"
        >
          {naoLidas > 99 ? "99+" : naoLidas}
        </span>
      )}
      {selected && <Check className="h-4 w-4 shrink-0 text-brand-600 dark:text-primary" />}
    </div>
  );
}
